/**
 * AK-938 — блок «Опыты» в карточке поля (low-fi 7·Б: на обзоре, без новой
 * вкладки) и в карточке хозяйства (7·А).
 */
import { useNavigate } from '@tanstack/react-router';
import { FlaskConical, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { pluralRu } from '@/lib/dateFmt';
import { useFieldTrials, useTrials } from '@/lib/science/api';
import { formatHa } from '@/lib/science/labels';
import { TrialStatusBadge } from './TrialStatusBadge';

const TRIAL_FORMS = ['опыт', 'опыта', 'опытов'] as const;

function BlockHeader({
  title,
  count,
  onPlace,
}: {
  title: string;
  count: number | null;
  onPlace?: () => void;
}) {
  return (
    <CardHeader className="p-4 pb-3">
      <div className="flex items-center gap-2">
        <CardTitle className="flex items-center gap-2 text-base leading-6">
          <FlaskConical className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
        {onPlace && (
          <Button variant="outline" size="sm" className="ml-auto" onClick={onPlace}>
            <Plus className="h-4 w-4" />
            Заложить опыт
          </Button>
        )}
      </div>
      {count != null && (
        <p className="text-sm text-muted-foreground">
          {count} {pluralRu(count, TRIAL_FORMS)}
        </p>
      )}
    </CardHeader>
  );
}

/** Карточка поля → «Опыты на поле». */
export function FieldTrialsBlock({
  fieldId,
  farmId,
  canPlace,
}: {
  fieldId: string;
  farmId?: string | null;
  canPlace: boolean;
}) {
  const navigate = useNavigate();
  const trials = useFieldTrials(fieldId);
  const rows = trials.data ?? [];
  return (
    <Card data-testid="field-trials-block">
      <BlockHeader
        title="Опыты на поле"
        count={trials.isLoading ? null : rows.length}
        onPlace={
          canPlace
            ? () =>
                void navigate({
                  to: '/science/place',
                  search: { field: fieldId, farm: farmId ?? undefined },
                })
            : undefined
        }
      />
      <CardContent className="overflow-x-auto p-4 pt-0">
        {trials.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : !rows.length ? (
          <p className="text-sm text-muted-foreground">На поле опытов нет.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Опыт</TableHead>
                <TableHead>Варианты</TableHead>
                <TableHead>Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow
                  key={t.id}
                  className="cursor-pointer"
                  onClick={() =>
                    void navigate({
                      to: '/science/trials/$fieldId/$trialId',
                      params: { fieldId, trialId: t.id },
                    })
                  }
                >
                  <TableCell className="font-medium">
                    {t.name}
                    <span className="block text-xs font-normal text-muted-foreground">
                      сезон {t.season_year}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {t.variants.length} ·{' '}
                    {formatHa(t.variants.reduce((s, v) => s + (v.area_ha ?? 0), 0))}
                  </TableCell>
                  <TableCell>
                    <TrialStatusBadge status={t.status} label={t.status_label} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/** Карточка хозяйства → «Опыты» (все сезоны, свежие сверху). */
export function FarmTrialsBlock({ farmId, canPlace }: { farmId: string; canPlace: boolean }) {
  const navigate = useNavigate();
  const trials = useTrials({ perPage: 100 });
  const rows = (trials.data?.items ?? []).filter((t) => t.farm_id === farmId && t.access === 'open');
  return (
    <Card data-testid="farm-trials-block">
      <BlockHeader
        title="Опыты"
        count={trials.isLoading ? null : rows.length}
        onPlace={
          canPlace
            ? () => void navigate({ to: '/science/place', search: { farm: farmId } })
            : undefined
        }
      />
      <CardContent className="overflow-x-auto p-4 pt-0">
        {trials.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : !rows.length ? (
          <p className="text-sm text-muted-foreground">В хозяйстве опытов нет.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Сезон</TableHead>
                <TableHead>Схема</TableHead>
                <TableHead>Поле</TableHead>
                <TableHead>Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow
                  key={`${t.field_tenant_id}:${t.field_trial_id}`}
                  className="cursor-pointer"
                  onClick={() =>
                    t.field_id && t.field_trial_id
                      ? void navigate({
                          to: '/science/trials/$fieldId/$trialId',
                          params: { fieldId: t.field_id, trialId: t.field_trial_id },
                        })
                      : undefined
                  }
                >
                  <TableCell>{t.season_year}</TableCell>
                  <TableCell>{t.scheme_name ?? t.name ?? '—'}</TableCell>
                  <TableCell>{t.field_label ?? '—'}</TableCell>
                  <TableCell>
                    <TrialStatusBadge status={t.status} label={t.status_label} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
